import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
   Input, Table, Space, Tooltip, Button, Popconfirm,
   Modal, Spin, Empty, Skeleton
} from 'antd';
import ResponsiveBreadcrumb from '../../components/breadcrumb/ResponsiveBreadcrumb';
import * as AntDIcons from '@ant-design/icons';
import NoticeBoardSlide from '../../components/notice-board/NoticeBoardSlide';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import uuid from 'react-uuid';
import moment from 'moment';

import Create from './Components/Create';
import Edit from './Components/Edit';
import Submissions from './Components/Submissions';
import '../common/BraineeModule.css';
import { decorateMicrocredentialRecord, isMicrocredentialHomework, recordMatchesMicrocredentialCourse } from './microcredentialUtils';
import { isPlagiarismCheckerEnabled } from './plagiarismUtils';

// NOTE: keep imports/locals minimal to avoid lint noise and improve readability

const ALLOWED_ROLES = ['super admin', 'admin', 'teacher', 'co-teacher'];

export function Homeworks(props) {
   const navigate = useNavigate();

   const search = window.location.search;
   const query = new URLSearchParams(search);
   const subject = query.get('id');
   const subjectname = query.get('name');
   const user_id = query.get('staff') ?? localStorage.getItem('selected-teacher') ?? Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const [searchText, setSearchText] = React.useState("");
   const [searchedColumn, setSearchedColumn] = React.useState("");
   const [homeworkList, setHomeworkList] = useState([]);
   const [levelSubmission, setLevelSubmission] = useState(null);
   const [plagiarismCheckerEnabled, setPlagiarismCheckerEnabled] = useState(false);
   const [homeworkListLoading, setHomeworkListLoading] = useState(false);
   const [isCompactListView, setIsCompactListView] = useState(() => window.innerWidth <= 768);
   const [level, setLevel] = useState(null);
   const [section, setSection] = useState(null);
   const [createModalVisible, setCreateModalVisible] = useState(false);
   const [editModalVisible, setEditModalVisible] = useState(false);
   const [hsModalVisible, setHSModalVisible] = useState(false);
   const [homeworkTitle, setHomeworkTitle] = useState("");
   const [homeworkID, setHomeoworkID] = useState("");
   const [homeworkData, setHomeworkData] = useState(null);
   const [creating, setCreating] = useState(false);
   const [updating, setUpdating] = useState(false);
   const user_role = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const isMicrocredentials = props.isMicrocredentials || isMicrocredentialHomework(props.type);
   const workType = isMicrocredentials ? 'microcredential' : 'homework';
   const moduleLabel = isMicrocredentials ? 'Homeworks & Projects' : 'Homework';
   const moduleListLabel = isMicrocredentials ? 'Homeworks & Projects Records' : 'Homework Records';
   const rootLabel = isMicrocredentials ? 'Brainee Microcredentials' : 'Brainee LMS';
   const subjectRoute = isMicrocredentials ? '/microcredentials/homeworks' : '/homeworksubjects';
   const heroKicker = isMicrocredentials ? 'Brainee Microcredentials' : 'Homework';

   const closeHSModal = useCallback(() => {
      setHSModalVisible(false);
   }, []);

   const handleSearchHomeworks = useCallback((userid) => {
      setHomeworkListLoading(true);
      var url = `${process.env.REACT_APP_API_HOMEWORK}/list?created_by=${userid}&level=${level}&section=${section}&subject=${subject}&type=${workType}`;

      axios.get(url).then((response) => {
         console.log(response);

         if (response.data) {
            const data = response.data;
            const filtered = isMicrocredentials
               ? data.filter((record) => recordMatchesMicrocredentialCourse(record, subject))
               : data;
            setHomeworkList(isMicrocredentials ? filtered.map((record) => decorateMicrocredentialRecord(record, { fallbackCourseName: subjectname })) : filtered);

            setHomeworkListLoading(false);
         }
      }, (error) => {
         setHomeworkListLoading(false);
         console.log(error.message);
      });
   }, [isMicrocredentials, level, section, subject, subjectname, workType]);

   useEffect(() => {
      Utils.verifyJwt();

      if (!ALLOWED_ROLES.includes(user_role.toLowerCase())) {
         navigate('/home');
         return;
      }

      handleSearchHomeworks(user_id);
   }, [handleSearchHomeworks, navigate, user_id, user_role]);

   useEffect(() => {
      const handleResize = () => {
         setIsCompactListView(window.innerWidth <= 768);
      };

      window.addEventListener('resize', handleResize);
      return () => {
         window.removeEventListener('resize', handleResize);
      };
   }, []);

   const handleSearch = useCallback((selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   }, []);

   const handleReset = useCallback((clearFilters) => {
      clearFilters();
      setSearchText("");
   }, []);

   const getColumnSearchProps = useCallback((dataIndex) => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               ref={node => {
                  //this.searchInput(node);
               }}
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<AntDIcons.SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}>
                  Search
               </Button>
               <Button
                  size="small"
                  style={{ width: 90 }}
                  // onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}
                  onClick={() => {
                     handleReset(clearFilters);
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Reset
               </Button>
            </Space>
         </div>
      ),
      filterIcon: filtered => <AntDIcons.SearchOutlined style={{ color: filtered ? '#033D89' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      render: text =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   }), [handleReset, handleSearch, searchText, searchedColumn]);

   const HomeworkListColumns = useMemo(() => [
      {
         title: 'Title',
         key: 'title',
         dataIndex: 'title',
         sorter: (a, b) => { return a.title.localeCompare(b.title) }, ...getColumnSearchProps('title'),
         ellipsis: {
            showTitle: false,
         },
         render: title => (
            <Tooltip placement="right" title={title}>
               <span style={{ cursor: "default" }}>{title}</span>
            </Tooltip>
         ),
         width: '20%'
      },
      {
         title: 'Level',
         key: 'level',
         dataIndex: 'level',
         sorter: (a, b) => { return (a.level === null ? '' : a.level).localeCompare(b.level === null ? '' : b.level) },
         width: '8%'
      },
      {
         title: 'Sections',
         key: 'section_names',
         dataIndex: 'section_names',
         sorter: (a, b) => { return (a.section_names === null ? '' : a.section_names).localeCompare(b.section_names === null ? '' : b.section_names) },
         ellipsis: {
            showTitle: false,
         },
         render: section_names => (
            <Tooltip placement="left" title={section_names}>
               <span style={{ cursor: "default" }}>{section_names}</span>
            </Tooltip>
         ),
         width: '14%'
      },
      {
         title: isMicrocredentials ? 'Course / Topic' : 'Subject',
         key: 'subject',
         dataIndex: 'subject',
         sorter: (a, b) => { return (a.subject || '').localeCompare(b.subject || '') }, ...getColumnSearchProps('subject'),
         ellipsis: {
            showTitle: false,
         },
         render: object => (
            <Tooltip placement="right" title={object}>
               <span style={{ cursor: "default" }}>{object}</span>
            </Tooltip>
         ),
         width: '14%'
      },
      {
         title: 'Created By',
         key: 'teacher',
         dataIndex: 'teacher',
         sorter: (a, b) => { return a.teacher.localeCompare(b.teacher) },
         ellipsis: {
            showTitle: false,
         },
         render: object => (
            <Tooltip placement="right" title={object}>
               <span style={{ cursor: "default" }}>{object}</span>
            </Tooltip>
         ),
         width: '12%'
      },
      {
         title: 'Homework Date',
         key: 'homework_date',
         dataIndex: 'homework_date',
         sorter: (a, b) => { return a.homework_date.localeCompare(b.homework_date) },
         width: '12%'
      },
      {
         title: 'Submit Date',
         key: 'submit_date',
         dataIndex: 'submit_date',
         sorter: (a, b) => { return a.submit_date.localeCompare(b.submit_date) },
         width: '12%'
      },
      {
         title: 'Action',
         key: 'action',
         render: payload => {
            return <>
               <Space size="small">
                  <Tooltip placement="top" title='Edit'>
                     <AntDIcons.EditOutlined color='#033D89' style={{ cursor: 'pointer' }}
                        onClick={() => handleEditHomework(payload.id)}
                     />
                  </Tooltip>
                  <Tooltip placement="top" title='Show Submissions'>
                     <AntDIcons.SolutionOutlined color='#033D89' style={{ cursor: 'pointer' }}
                        onClick={() => handleHomeworkSubmissions(payload.id, payload.class_id, payload.title, payload.plagiarism_checker)}
                     />
                  </Tooltip>
                  <Tooltip placement="top" title='Duplicate'>
                     <Popconfirm
                        title="Create a copy of this homework?"
                        onConfirm={() => handleDuplicateHomework(payload.id)}
                        okText="Yes"
                        cancelText="No"
                     >
                        <AntDIcons.CopyOutlined color={'#033D89'} style={{ cursor: 'pointer' }} />
                     </Popconfirm>
                  </Tooltip>
                  <Tooltip placement="top" title='Remove'>
                     <Popconfirm
                        title="Are you sure you want to delete this homework?"
                        onConfirm={() => handleRemoveHomework(payload.id)}
                        okText="Yes"
                        cancelText="No"
                     >
                        <AntDIcons.DeleteOutlined color='#033D89' style={{ cursor: 'pointer' }} />
                     </Popconfirm>
                  </Tooltip>
               </Space>
            </>
         },
         width: '8%'
      },
   ].filter(item => !item.hidden), [getColumnSearchProps]);

   const handleEditHomework = useCallback((id) => {
      setHomeoworkID(id);
      setEditModalVisible(true);
   }, []);

   const handleHomeworkSubmissions = useCallback((homework_id, level_id, title, plagiarismChecker) => {
      setHomeworkTitle(title);
      setHomeoworkID(homework_id);
      setLevelSubmission(level_id);
      setPlagiarismCheckerEnabled(isPlagiarismCheckerEnabled(plagiarismChecker));
      setHSModalVisible(true);
   }, []);

   const handleRemoveHomework = useCallback((id) => {
      setHomeworkListLoading(true);

      var url = `${process.env.REACT_APP_API_HOMEWORK}/remove?id=${id}`;
      axios.post(url).then(response => {
         if (response.data) {
            Modal.success({
               title: 'Homework',
               content: 'Homework successfully removed.',
               centered: true,
               onOk: () => handleSearchHomeworks(user_id)
            });
         }
      }, (error) => {
         //
         setHomeworkListLoading(false);
         Modal.error({
            title: 'Homework',
            content: error.response.data,
            centered: true,
         });
      });
   }, [handleSearchHomeworks, user_id]);

   const handleCancelCreate = useCallback(() => {
      setCreateModalVisible(false);
   }, []);

   const handleOnFinishCreate = useCallback((homeworkData, fileForUpload) => {
      setCreating(true);

      if (fileForUpload.length > 0) {
         var formData = new FormData();

         fileForUpload.forEach(file => {
            // the name has to be 'files' so that .NET could properly bind it (Phuck this sheet! Took me the whole day to find this sheet!)
            formData.append('files', file);
         });

         var uploadedfiles = "";
         var url = `${process.env.REACT_APP_API_S3_UPLOAD}/uploadMultiple?s3path=${process.env.REACT_APP_S3_HOMEWORKS_TEACHERS_PATH}`;
         axios.post(url, formData).then(response => {

            if (response.data) {
               uploadedfiles = response.data;

               homeworkData.document = uploadedfiles;

               var url = `${process.env.REACT_APP_API_HOMEWORK}/add`;
               axios.post(url, homeworkData).then(response => {
                  setCreating(false);
                  if (response.data) {
                     Modal.success({
                        title: 'Homework',
                        content: 'Homework created.',
                        centered: true,
                        onOk: () => { setCreateModalVisible(false) },
                     });

                     handleSearchHomeworks(user_id);
                  }
               }, (error) => {
                  setCreating(false);
                  Modal.error({
                     title: 'Homework',
                     content: error.response.data,
                     centered: true,
                     // onOk: () => { setCreateModalVisible(false) },
                  });
               });

            }
         }, (error) => {
            // console.log(error.message);
         });
      } else {
         var url = `${process.env.REACT_APP_API_HOMEWORK}/add`;
         axios.post(url, homeworkData).then(response => {
            setCreating(false);
            if (response.data) {
               Modal.success({
                  title: 'Homework',
                  content: 'Homework created.',
                  centered: true,
                  onOk: () => { setCreateModalVisible(false) },
               });

               handleSearchHomeworks(user_id);
            }

         }, (error) => {
            setCreating(false);
            Modal.error({
               title: 'Homework',
               content: error.response.data,
               centered: true,
               // onOk: () => { setCreateModalVisible(false) },
            });
         });
      }
   }, [handleSearchHomeworks, user_id]);

   const handleCancelEdit = useCallback(() => {
      setEditModalVisible(false);
   }, []);

   const handleOnFinishEdit = useCallback((homeworkData, filesForUpload) => {
      // message.info("Blah! Blah! Blah!");
      // setEditModalVisible(false);


      setUpdating(true);

      if (filesForUpload.length > 0) {
         var formData = new FormData();

         filesForUpload.forEach(file => {
            // the name has to be 'files' so that .NET could properly bind it (Phuck this sheet! Took me the whole day to figure out this sheet!)
            formData.append('files', file);
         });

         var uploadedfiles = "";
         var url = `${process.env.REACT_APP_API_S3_UPLOAD}/uploadMultiple?s3path=${process.env.REACT_APP_S3_HOMEWORKS_TEACHERS_PATH}`;
         axios.post(url, formData).then(response => {
            console.log(response);
            if (response.data) {
               uploadedfiles = response.data;

               homeworkData.document += homeworkData.document !== "" ? "," + uploadedfiles : uploadedfiles;

               var url = `${process.env.REACT_APP_API_HOMEWORK}/update`;
               axios.post(url, homeworkData).then(response => {
                  // console.log(response);

                  if (response.data) {
                     setUpdating(false);
                     Modal.success({
                        title: 'Homework',
                        content: 'Homework updated.',
                        centered: true,
                        onOk: () => { setEditModalVisible(false) },
                     });

                     handleSearchHomeworks(user_id);
                  }
               }, (error) => {
                  // console.log(error.message);
                  setUpdating(false);
                  Modal.error({
                     title: 'Homework',
                     content: error.response.data,
                     centered: true
                  });
               });

            }
         }, (error) => {
            // console.log(error.message);
         });
      } else {
         var url = `${process.env.REACT_APP_API_HOMEWORK}/update`;
         axios.post(url, homeworkData).then(response => {
            console.log(response)
            if (response.data) {
               setUpdating(false);
               Modal.success({
                  title: 'Homework',
                  content: 'Homework updated.',
                  centered: true,
                  onOk: () => { setEditModalVisible(false) },
               });

               handleSearchHomeworks(user_id);
            }

         }, (error) => {
            console.log(error.message);
            setUpdating(false);
            Modal.error({
               title: 'Homework',
               content: error.response.data,
               centered: true,
               // onOk: () => { setEditModalVisible(false) },
            });
         });
      }
   }, [handleSearchHomeworks, user_id]);

   const handleOnEvaluate = useCallback(() => {

   }, []);

   // const handleUserChange = (id) => {
   //    handleSearchHomeworks(id);
   //    setUserId(id);
   // }

   const handleDuplicateHomework = useCallback((id) => {
      axios.get(`${process.env.REACT_APP_API_HOMEWORK}/info?id=${id}`).then((response) => {
         if (response.data) {
            const homeworkdata = response.data[0];

            var o2o_mode = Utils.determineOnlineOffline(); //window.location.hostname.match(/localhost|[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}|::1|\.local|^$/gi) ? "offline" : "online";
            var homework_id = "homework_" + o2o_mode + "_" + uuid();
            var utc = new Date().toJSON().slice(0, 19);

            let hwData = {
               id: homework_id,
               class_id: homeworkdata.class_id,
               session_id: 0, //api will take care of this
               title: homeworkdata.title + " - (" + utc + ")",
               homework_date: moment(homeworkdata.homework_date).format('YYYY-MM-DD HH:mm:ss'),
               submit_date: moment(homeworkdata.submit_date).format('YYYY-MM-DD HH:mm:ss'),
               subject_id: homeworkdata.subject_id,
               subject_code: homeworkdata.subject_code ?? '',
               description: homeworkdata.description,
               created_by: homeworkdata.created_by,
               document: homeworkdata.document,
               sections: '', //section.join(','),
               assigned_students: '', //homeworkdata.assigned_students,
               students: '', //homeworkdata.students,
               max_points: homeworkdata.max_points,
               type: homeworkdata.type,
               plagiarism_checker: isPlagiarismCheckerEnabled(homeworkdata.plagiarism_checker)
            }

            var url = `${process.env.REACT_APP_API_HOMEWORK}/add`;
            axios.post(url, hwData).then(response => {
               setCreating(false);
               if (response.data) {
                  Modal.success({
                     title: 'Homework',
                     content: 'Homework duplicated.',
                     centered: true,
                  });

                  handleSearchHomeworks(user_id);
               }
            }, (error) => {
               setCreating(false);
               Modal.error({
                  title: 'Homework',
                  content: error.response.data,
                  centered: true,
               });
            });
         }
      }, (error) => {
         console.log(error.message);
      });
   }, [handleSearchHomeworks, user_id]);

   const renderTableSkeleton = () => (
      <div className="brainee-module-skeleton-table">
         <div className="brainee-module-skeleton-header">
            <Skeleton.Input active size="small" className="brainee-module-skeleton-title" />
            <Skeleton.Input active size="small" className="brainee-module-skeleton-chip" />
            <Skeleton.Input active size="small" className="brainee-module-skeleton-chip" />
            <Skeleton.Button active size="small" className="brainee-module-skeleton-action" />
         </div>
         {Array.from({ length: 7 }).map((_, index) => (
            <div className="brainee-module-skeleton-row" key={`homeworks-table-skeleton-${index}`}>
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell is-wide" />
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell" />
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell" />
               <Skeleton.Button active size="small" className="brainee-module-skeleton-action" />
            </div>
         ))}
      </div>
   );

   const getCardValue = (value) => {
      if (value === null || value === undefined || value === '') {
         return '—';
      }

      return value;
   };

   const renderCardField = (label, value, { fullWidth = false } = {}) => (
      <div className={`brainee-module-record-field${fullWidth ? ' is-full' : ''}`}>
         <span className="brainee-module-record-label">{label}</span>
         <span className="brainee-module-record-value">{getCardValue(value)}</span>
      </div>
   );

   const renderHomeworkCardActions = (payload) => (
      <Space size="small">
         <Tooltip placement="top" title='Edit'>
            <AntDIcons.EditOutlined color='#033D89' style={{ cursor: 'pointer' }} onClick={() => handleEditHomework(payload.id)} />
         </Tooltip>
         <Tooltip placement="top" title='Show Submissions'>
            <AntDIcons.SolutionOutlined color='#033D89' style={{ cursor: 'pointer' }} onClick={() => handleHomeworkSubmissions(payload.id, payload.class_id, payload.title, payload.plagiarism_checker)} />
         </Tooltip>
         <Tooltip placement="top" title='Duplicate'>
            <Popconfirm
               title="Create a copy of this homework?"
               onConfirm={() => handleDuplicateHomework(payload.id)}
               okText="Yes"
               cancelText="No"
            >
               <AntDIcons.CopyOutlined color={'#033D89'} style={{ cursor: 'pointer' }} />
            </Popconfirm>
         </Tooltip>
         <Tooltip placement="top" title='Remove'>
            <Popconfirm
               title="Are you sure you want to delete this homework?"
               onConfirm={() => handleRemoveHomework(payload.id)}
               okText="Yes"
               cancelText="No"
            >
               <AntDIcons.DeleteOutlined color='#033D89' style={{ cursor: 'pointer' }} />
            </Popconfirm>
         </Tooltip>
      </Space>
   );

   const renderHomeworkCardList = () => (
      <div className="brainee-module-record-list-region">
         <div className="brainee-module-record-list">
            {homeworkList.map((record, index) => {
               const cardKey = record.uid || record.id || `homework-card-${index}`;

               return (
                  <article className="brainee-module-record-card" key={cardKey}>
                     <div className="brainee-module-record-header">
                        <div className="brainee-module-record-heading">
                           <div className="brainee-module-record-header-row">
                              <span className="brainee-module-record-kicker">{moduleLabel}</span>
                              <span className="brainee-module-record-pill">{getCardValue(record.homework_date)}</span>
                           </div>
                           <h3>{getCardValue(record.title)}</h3>
                           <p>Submit by {getCardValue(record.submit_date)}</p>
                        </div>
                     </div>
                     <div className="brainee-module-record-meta">
                        {renderCardField('Level', record.level)}
                        {renderCardField('Sections', record.section_names, { fullWidth: true })}
                        {renderCardField(isMicrocredentials ? 'Course' : 'Subject', record.subject, { fullWidth: true })}
                        {renderCardField('Created By', record.teacher)}
                     </div>
                     <div className="brainee-module-record-actions">
                        {renderHomeworkCardActions(record)}
                     </div>
                  </article>
               );
            })}
         </div>
         <div className="brainee-module-record-list-footer">Total Records: {homeworkList.length}</div>
      </div>
   );
   return (
      <>
         <div className="dashboard-area-all brainee-module-page">
            <div className="dashboard-item brainee-module-shell">
               <div className="brainee-module-breadcrumb">
                  <ResponsiveBreadcrumb
                     items={[
                        { key: 'home', node: <Link to="/home">Home</Link> },
                        { key: 'lms', node: rootLabel },
                        { key: 'hw', node: <Link to={subjectRoute}>{moduleLabel}</Link> },
                        { key: 'subject', node: subjectname },
                     ]}
                  />
               </div>

               <section className="brainee-module-hero">
                  <div>
                     <span className="brainee-module-kicker">{heroKicker}</span>
                     <h1>{subjectname || (isMicrocredentials ? 'Homeworks & Projects List' : 'Homework List')}</h1>
                     <p>{isMicrocredentials ? 'Review course-based homeworks & projects, monitor submissions, and keep microcredential tasks organized by topic.' : 'Review homework activities, monitor submissions, and manage classroom work for this subject.'}</p>
                     <div className="brainee-module-meta">
                        <span><AntDIcons.BookOutlined /> {homeworkListLoading ? 'Loading records' : `${homeworkList.length} records`}</span>
                        <span><AntDIcons.FileTextOutlined /> {moduleLabel}</span>
                     </div>
                  </div>
               </section>

               <section className="brainee-module-panel">
                  <div className="brainee-module-panel-header">
                     <div>
                        <span className="brainee-module-kicker">Manage</span>
                        <h2>{moduleListLabel}</h2>
                     </div>
                     <span className="brainee-module-count">{homeworkListLoading ? '...' : homeworkList.length}</span>
                  </div>

                  {homeworkListLoading
                     ? renderTableSkeleton()
                     : homeworkList.length > 0
                        ? (
                           isCompactListView
                              ? renderHomeworkCardList()
                              : <div className="brainee-module-table-shell" style={{height: `calc(100vh - 25.5rem)`}}>
                                 <Table
                                    className="brainee-module-table"
                                    rowKey={'id'}
                                    size='small'
                                    columns={HomeworkListColumns}
                                    dataSource={homeworkList}
                                    pagination={false}
                                    scroll={{
                                       x: '1000px',
                                       y: 'calc(100vh - 30.5rem)',
                                    }}
                                    footer={() => <div className="brainee-module-table-footer">Total Records: {homeworkList.length}</div>}
                                 />
                              </div>
                        )
                        : <div className='brainee-module-state'>
                           <Empty image="../../images/brainee_mascot.png" description={isMicrocredentials ? 'No microcredential homeworks or projects found.' : 'No records found'} />
                        </div>
                  }
               </section>
            </div>
         </div>

         <Modal
            title={moduleLabel}
            // className="homework-assignment-modal"
            centered
            destroyOnClose={true}
            onCancel={handleCancelCreate}
            open={createModalVisible}
            width={1080}
            footer={[
               <Button key="cancel" onClick={handleCancelCreate} disabled={creating}>Cancel</Button>,
               <Button key="save" type="primary" htmlType="submit" form="homework-create-form" loading={creating}>Save</Button>,
            ]}
         >
            <Spin spinning={creating} tip={`Creating ${moduleLabel.toLowerCase()}, please wait...`}>
               <Create
                  formId="homework-create-form"
                  onCancelCallback={handleCancelCreate}
                  onFinishCallback={handleOnFinishCreate}
                  type={workType}
               />
            </Spin>
         </Modal>

         <Modal
            title={`Edit ${moduleLabel}`}
            // className="homework-assignment-modal"
            centered
            destroyOnClose={true}
            onCancel={handleCancelEdit}
            open={editModalVisible}
            width={1080}
            footer={[
               <Button key="cancel" onClick={handleCancelEdit} disabled={updating}>Cancel</Button>,
               <Button key="save" type="primary" htmlType="submit" form="homework-edit-form" loading={updating}>Save</Button>,
            ]}
         >
            <Spin spinning={updating} tip={"Processing, please wait..."}>
               <Edit
                  formId="homework-edit-form"
                  homeworkID={homeworkID}
                  homeworkData={homeworkData}
                  onCancelCallback={handleCancelEdit}
                  onFinishCallback={handleOnFinishEdit}
                  type={workType}
               />
            </Spin>
         </Modal>

         <Modal
            title={`${moduleLabel} Submissions`}
            // className="homework-assignment-modal homework-submissions-modal"
            // okText={"Close"}
            // cancelButtonProps={{ style: { display: 'none' } }}
            // okButtonProps={{ shape: "round" }}
            cancelText={"Close"}
            okButtonProps={{ style: { display: 'none' } }}
            centered
            destroyOnClose={true}
            open={hsModalVisible}
            // width={window.innerWidth - 300}
            width='90%'
            onCancel={closeHSModal}
            // onOk={closeHSModal}
         >
            {/* <Spin spinning={updating} tip={"Loading, please wait..."}> */}
            <Submissions
               homeworkTitle={homeworkTitle}
               homeworkID={homeworkID}
               levelID={levelSubmission}
               plagiarismCheckerEnabled={plagiarismCheckerEnabled}
               onEvaluateCallback={handleOnEvaluate}
               type={workType}
            />
            {/* </Spin> */}
         </Modal>
      </>
   );
}




